import React from 'react';
import {
    Box,
    Text,
    VStack,
    Heading,
    ChakraProvider,
    List,
    ListItem,
    Link,
} from '@chakra-ui/react';
import TopHeaderBar from './components/Top';
import NavigationHeader from './components/NavigationHeader';
import FooterBar from './components/GetAQuote';
import Footer from './components/Footer';

function PrivacyPolicy() {
    return (
        <ChakraProvider>
            <TopHeaderBar />
            <NavigationHeader />
            <Box minHeight="calc(100vh - 4rem - 4rem)" backgroundColor="gray.100" paddingTop={"174px"}>
                <VStack
                    as="section"
                    spacing="6"
                    alignItems="start"
                    maxW="7xl"
                    px={{ base: '4', md: '8', lg: '16' }}
                    py="12"
                    mx="auto"
                    backgroundColor="white"
                    borderRadius="lg"
                    boxShadow="lg"
                    p={{ base: '4', md: '8', lg: '16' }}
                >
                    <Heading as="h1" size="2xl" color="gray.800" mb={2}>
                        Privacy Policy
                    </Heading>
                    <Text fontSize="md" color="gray.600" mb={8}>
                        Last Updated: April 2025
                    </Text>

                    <Text fontSize="md" color="gray.700" mb={8} lineHeight="tall">
                        Lia Insurance Agency ("we," "our," or "us") is committed to protecting your privacy. This policy explains how we collect, use, disclose, and safeguard your information when you visit{' '}
                        <Link href="https://liainsuranceagency.com" color="teal.500" isExternal>
                            liainsuranceagency.com
                        </Link>{' '}
                        or interact with our services.
                    </Text>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            1. Information We Collect
                        </Heading>

                        <Heading as="h3" size="lg" color="gray.700" mb={3} mt={4}>
                            Information You Provide
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            We may collect personal information you voluntarily provide, including:
                        </Text>
                        <List spacing={2} mb={6} pl={6}>
                            <ListItem color="gray.700">Name</ListItem>
                            <ListItem color="gray.700">Email address</ListItem>
                            <ListItem color="gray.700">Phone number</ListItem>
                            <ListItem color="gray.700">Address</ListItem>
                            <ListItem color="gray.700">Policy details</ListItem>
                            <ListItem color="gray.700">Vehicle or property information for quoting</ListItem>
                            <ListItem color="gray.700">Any information submitted through website forms or chat tools</ListItem>
                        </List>

                        <Heading as="h3" size="lg" color="gray.700" mb={3} mt={4}>
                            Information Collected Automatically
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            When you use our website, we may automatically collect:
                        </Text>
                        <List spacing={2} mb={6} pl={6}>
                            <ListItem color="gray.700">IP address</ListItem>
                            <ListItem color="gray.700">Device and browser information</ListItem>
                            <ListItem color="gray.700">Pages visited and time spent on the site</ListItem>
                            <ListItem color="gray.700">Cookies and tracking data used for analytics or website performance</ListItem>
                        </List>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            2. How We Use Your Information
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            We use the information we collect for purposes such as:
                        </Text>
                        <List spacing={2} mb={6} pl={6}>
                            <ListItem color="gray.700">Providing insurance quotes</ListItem>
                            <ListItem color="gray.700">Responding to inquiries</ListItem>
                            <ListItem color="gray.700">Processing applications</ListItem>
                            <ListItem color="gray.700">Communicating about policy updates, renewal reminders, or service notifications</ListItem>
                            <ListItem color="gray.700">Improving website performance and user experience</ListItem>
                            <ListItem color="gray.700">Complying with legal or regulatory obligations</ListItem>
                        </List>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            3. SMS, Email, and Phone Communications
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            If you submit your contact information, you may receive messages related to:
                        </Text>
                        <List spacing={2} mb={4} pl={6}>
                            <ListItem color="gray.700">Quotes</ListItem>
                            <ListItem color="gray.700">Policy information</ListItem>
                            <ListItem color="gray.700">Appointment confirmations</ListItem>
                            <ListItem color="gray.700">Service updates</ListItem>
                        </List>
                        <Text fontSize="md" color="gray.700" mb={4} lineHeight="tall">
                            Message frequency may vary. Message and data rates may apply. Reply HELP for assistance or{' '}
                            <Link href="#contact" color="teal.500">
                                STOP to opt out
                            </Link>.
                        </Text>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            You may also receive marketing messages only if you provide express consent. You may opt out at any time.
                        </Text>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            4. Sharing Your Information
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            We may share your information with:
                        </Text>
                        <List spacing={2} mb={4} pl={6}>
                            <ListItem color="gray.700">Insurance carriers for quoting or policy servicing</ListItem>
                            <ListItem color="gray.700">Vendors that help operate our website or communication systems</ListItem>
                            <ListItem color="gray.700">Compliance or regulatory entities when required by law</ListItem>
                        </List>
                        <Text fontSize="md" color="gray.700" mb={6} fontWeight="semibold">
                            We do not sell your personal information.
                        </Text>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            5. Cookies and Tracking Technologies
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            We use cookies and analytics tools to understand website usage. You may disable cookies through your browser settings. Some features may not function fully without cookies.
                        </Text>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            6. Data Security
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            We take reasonable administrative and technical measures to protect your information. While no system is fully secure, we work to safeguard your data from unauthorized access or disclosure.
                        </Text>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            7. Third-Party Links
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            Our website may contain links to third-party sites. We are not responsible for the privacy practices of those websites.
                        </Text>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            8. Children's Privacy
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            Our website is not intended for individuals under 13. We do not knowingly collect data from children.
                        </Text>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            9. Your Rights and Choices
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            Depending on your state, you may have rights to:
                        </Text>
                        <List spacing={2} mb={4} pl={6}>
                            <ListItem color="gray.700">Access the information we have collected</ListItem>
                            <ListItem color="gray.700">Request updates or corrections</ListItem>
                            <ListItem color="gray.700">Request deletion where applicable</ListItem>
                            <ListItem color="gray.700">Opt out of marketing communications</ListItem>
                        </List>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            To exercise these rights, use our contact information below.
                        </Text>
                    </Box>

                    <Box w="100%" id="contact">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            10. Contact Us
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={3}>
                            For questions about this Privacy Policy or our data practices, contact:
                        </Text>
                        <VStack alignItems="start" spacing={2} mb={6} pl={4}>
                            <Text fontSize="md" color="gray.700" fontWeight="semibold">
                                Lia Insurance Agency
                            </Text>
                            <Text fontSize="md" color="gray.700">
                                Email:{' '}
                                <Link href="mailto:insurance@liagroup.com" color="teal.500">
                                    insurance@liagroup.com
                                </Link>
                            </Text>
                            <Text fontSize="md" color="gray.700">
                                Phone:{' '}
                                <Link href="tel:518-579-3153" color="teal.500">
                                    518-579-3153
                                </Link>
                            </Text>
                        </VStack>
                    </Box>

                    <Box w="100%">
                        <Heading as="h2" size="xl" color="gray.800" mb={4} mt={6}>
                            11. Updates to This Policy
                        </Heading>
                        <Text fontSize="md" color="gray.700" mb={6} lineHeight="tall">
                            We may update this Privacy Policy from time to time. Changes are effective upon posting the new version on this website.
                        </Text>
                    </Box>
                </VStack>
            </Box>
            <FooterBar />
            <Footer mb={12} />
        </ChakraProvider>
    );
}

export default PrivacyPolicy;
